import { useCallback, useEffect, useState } from 'react';

// Which child of a horizontally scrolling track is nearest its left edge, and
// whether the track scrolls at all. The only JavaScript a native scroll-snap
// row needs: the browser owns the scrolling, this reads where it ended up.
//
// Shared by SnapRow and ProductGallery so there is one measurement, not two
// copies drifting apart. `deps` re-measures when the children change.
export default function useSnapIndex(trackRef, deps = []) {
  const [index, setIndex] = useState(0);
  const [canScroll, setCanScroll] = useState(false);

  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const kids = [...track.children];
    if (!kids.length) return;
    // Measured on screen rather than via offsetLeft, so it holds whichever
    // element the children happen to be positioned against.
    const edge = track.getBoundingClientRect().left;
    let best = 0;
    let bestDist = Infinity;
    kids.forEach((k, i) => {
      const d = Math.abs(k.getBoundingClientRect().left - edge);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setIndex((prev) => (prev === best ? prev : best));
    setCanScroll(track.scrollWidth > track.clientWidth + 2);
  }, [trackRef]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; measure(); });
    };
    measure();
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', measure);
      if (frame) cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, ...deps]);

  return { index, canScroll };
}
